import { useMemo } from "react";
import { useSimpleRegistrationContext } from "pages/SimpleRegistrationsPage";
import { Box as MuiBox, Button as MuiButton, styled } from "@mui/material";
import { SlideModal } from "components/Modal/SlideModal";
import { ConfirmModal } from "components/Modal/ConfirmModal";
import { Container } from "components/SimpleRegistrations/Container";
import { Header } from "components/SimpleRegistrations/Header";
import { Body } from "components/SimpleRegistrations/Body";
import { useImperativeModal } from "~/ImperativeModalProvider";

const Box = styled(MuiBox)`
  &.container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &.title {
    font-size: 3vw;
    font-weight: bold;

    b {
      font-weight: 800;
    }
  }

  &.title-landscape {
    margin-bottom: 1vw;
  }

  &.title-portrait {
    margin-bottom: 3vw;
  }

  &.alert-body {
    font-weight: bold;
  }

  &.alert-title {
    font-size: 2vw;
    margin-bottom: 5%;
  }

  &.disclaimer {
    font-size: 3vw;
    color: #bbbbbb;
  }

  &.disclaimer-landscape {
    font-size: 2vw;
  }

  &.disclaimer-portrait {
    font-size: 3vw;
  }

  &.customer-phone-number {
    border: solid 1px #dee2ec;
    background: #f3f8ff;
    border-radius: 4px;
    padding: 2%;
    font-size: 3vw;
    display: flex;
    align-items: center;
    font-weight: bold;
    margin: 3% 0;
    height: 8vh;
    justify-content: center;

    &.landscape {
      height: 10vh;
    }
  }

  &.btn-wrapper {
    display: flex;
    margin-bottom: 6%;
    padding: 0 3%;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    gap: 20px;
  }
`;

const Button = styled(MuiButton)`
  width: 100%;
  height: 10vh;
  font-size: 3vw;

  &.cancel-btn {
    color: #000000;
    border-color: #000000;
  }
`;

interface ConfirmPhoneNumberProps {
  open: boolean;
  onClose: (response?: boolean) => void;
}

export function ConfirmPhoneNumberModal({
  open,
  onClose,
}: ConfirmPhoneNumberProps) {
  const { isLandscape, phoneNumber } = useSimpleRegistrationContext();
  const imperativeModal = useImperativeModal();

  const displayPhoneNumber = useMemo(() => {
    let value = phoneNumber.substring(0, 11).padEnd(11, "0");

    if (phoneNumber.length < 2) {
      value = value.substring(0, 1) + "1" + value.substring(2);
    }

    return value.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  }, [phoneNumber]);

  const handleConfirm = async () => {
    await imperativeModal.open((close) => (
      <ConfirmModal
        body={
          <Box className="alert-body">
            <Box className="alert-title">안내</Box>
            <div>
              이전 단계로 이동할까요?
              <br />
              입력중인 정보는 저장되지 않습니다.
            </div>
          </Box>
        }
        cancelText="아니오"
        confirmText="예"
        onClose={close}
        onConfirm={() => {
          onClose(false);
          close();
        }}
        open={true}
      />
    ));
  };

  const layoutMode = isLandscape ? "landscape" : "portrait";

  return (
    <SlideModal open={open}>
      <Container>
        <Header onClose={onClose} onConfirm={handleConfirm} />
        <Body>
          <Box className="container">
            <div>
              <Box className={`title title-${layoutMode}`}>
                입력한 <b>휴대폰 번호를 확인</b>해주세요.
              </Box>
              <Box className={`customer-phone-number ${layoutMode}`}>
                {displayPhoneNumber}
              </Box>
              <Box className={`disclaimer disclaimer-${layoutMode}`}>
                ∙ 본인이 아닌 정보로 접수한 경우 법적 책임은 접수한 당사자에게
                있습니다.
                <br />
                ∙ [접수하기]을 누르시면 새로운 고객으로 접수가 진행됩니다.
                <br />∙ 수정이 필요하시면 [취소] 후 다시 입력해주세요.
              </Box>
            </div>
          </Box>
        </Body>
        <Box className="btn-wrapper">
          <Button
            variant="outlined"
            className="cancel-btn"
            onClick={() => handleConfirm()}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onClose(true);
            }}
          >
            접수하기
          </Button>
        </Box>
      </Container>
    </SlideModal>
  );
}
